import React, { useCallback, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    type SelectChangeEvent,
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import { streamChat, type ChatMessage, type ChatModel } from '../api/chatApi';

// Working-on-this-account models first (Sonnet 4.6 is the default; Opus 4.6
// also invokes). Sonnet 5 / Opus 5 are kept selectable so they light up when
// AWS ungates them; picking one before then returns the Bedrock AccessDenied
// error.
const MODEL_OPTIONS: { value: ChatModel; label: string }[] = [
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
    { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
    { value: 'claude-opus-5', label: 'Claude Opus 5' },
];

// Width of the pane when open, and of the collapsed rail. The rail stays
// visible so the assistant is always one click away regardless of which tab
// is active (HOME-233 follow-up: assistant promoted out of the tab group into
// a persistent right-hand pane, à la AWS Q but on the RHS).
const OPEN_WIDTH = 400;
const RAIL_WIDTH = 48;
// Clearance for the fixed footer bar (see Footer.tsx) so the input row isn't
// hidden behind it.
const FOOTER_CLEARANCE = 32;

const AssistantPane: React.FC = () => {
    const auth = useAuth();
    const [open, setOpen] = useState(true);
    const [model, setModel] = useState<ChatModel>('claude-sonnet-5');
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [streaming, setStreaming] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const streamingTextRef = useRef('');

    const token = auth.user?.access_token ?? '';

    const send = useCallback(async () => {
        const text = input.trim();
        if (!text || streaming) return;

        setError(undefined);
        setInput('');
        const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: text }];
        setMessages([...nextMessages, { role: 'assistant', content: '' }]);
        setStreaming(true);
        streamingTextRef.current = '';

        try {
            await streamChat(token, nextMessages, model, (chunk) => {
                streamingTextRef.current += chunk;
                setMessages((prev) => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: 'assistant', content: streamingTextRef.current };
                    return updated;
                });
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setStreaming(false);
        }
    }, [input, messages, model, streaming, token]);

    // Collapsed: a slim rail pinned to the right edge with a single affordance
    // to reopen. Kept sticky/full-height so it never scrolls away.
    if (!open) {
        return (
            <Box
                component='aside'
                aria-label='Assistant (collapsed)'
                sx={{
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    height: '100vh',
                    width: RAIL_WIDTH,
                    flexShrink: 0,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    pt: 1,
                    gap: 1,
                }}
            >
                <Tooltip title='Open assistant' placement='left'>
                    <IconButton size='small' onClick={() => setOpen(true)} aria-label='Open assistant'>
                        <ChatBubbleOutlineRoundedIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
                <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ writingMode: 'vertical-rl', mt: 1, letterSpacing: 1, textTransform: 'uppercase' }}
                >
                    Assistant
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            component='aside'
            aria-label='Assistant'
            sx={{
                position: 'sticky',
                top: 0,
                alignSelf: 'flex-start',
                height: '100vh',
                width: { xs: '100%', sm: OPEN_WIDTH },
                flexShrink: 0,
                borderLeft: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                pb: `${FOOTER_CLEARANCE}px`,
            }}
        >
            <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ px: 2, py: 1.25, flexShrink: 0 }}
            >
                <ChatBubbleOutlineRoundedIcon fontSize='small' color='primary' />
                <Typography variant='subtitle1' sx={{ flexGrow: 1, fontWeight: 600 }}>
                    Assistant
                </Typography>
                <Select
                    size='small'
                    value={model}
                    onChange={(e: SelectChangeEvent) => setModel(e.target.value as ChatModel)}
                    sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' } }}
                >
                    {MODEL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
                <Tooltip title='Collapse assistant' placement='bottom'>
                    <IconButton size='small' onClick={() => setOpen(false)} aria-label='Collapse assistant'>
                        <KeyboardDoubleArrowRightRoundedIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Divider />

            {error && <Alert severity='error' sx={{ m: 1.5, mb: 0 }}>{error}</Alert>}

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                {messages.length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                        Ask about Martin&apos;s home infrastructure.
                    </Typography>
                )}
                <Stack spacing={1.5}>
                    {messages.map((m, i) => (
                        <Paper
                            key={i}
                            variant='outlined'
                            sx={{
                                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                                maxWidth: '90%',
                                px: 1.5,
                                py: 1,
                                bgcolor: m.role === 'user' ? 'action.hover' : 'background.default',
                            }}
                        >
                            <Typography variant='caption' color='text.secondary'>
                                {m.role === 'user' ? 'You' : 'Assistant'}
                            </Typography>
                            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                                {m.content || (streaming && i === messages.length - 1 ? '…' : '')}
                            </Typography>
                        </Paper>
                    ))}
                </Stack>
            </Box>

            <Divider />

            <Stack direction='row' spacing={1} sx={{ p: 1.5, flexShrink: 0 }}>
                <TextField
                    fullWidth
                    size='small'
                    multiline
                    maxRows={4}
                    placeholder='Ask a question…'
                    value={input}
                    disabled={streaming}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            void send();
                        }
                    }}
                />
                <Button variant='contained' onClick={() => void send()} disabled={streaming || !input.trim()}>
                    {streaming ? <CircularProgress size={20} /> : 'Send'}
                </Button>
            </Stack>
        </Box>
    );
};

export default AssistantPane;
